import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import { SubscriptionPlan } from '@mono-repo-starter/types';
import { toast as sonnerToast } from 'sonner';

// The function that fetches the data from the Firebase backend
export const fetchSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  try {
    const getPlansFn = httpsCallable<void, { success: boolean; plans: SubscriptionPlan[]; error?: string }>(
      firebaseFunctions,
      'getSubscriptionPlans'
    );
    const result = await getPlansFn();
    if (result.data.success && result.data.plans) {
      return result.data.plans;
    } else {
      throw new Error(result.data.error || 'Could not load subscription plans.');
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error("Error fetching subscription plans:", error);
    sonnerToast.error(`Error fetching plans: ${errorMessage}`);
    // Re-throw the error to let React Query handle the error state
    throw error;
  }
};

// The custom hook that uses React Query
export const useSubscriptionPlans = () => {
  return useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: fetchSubscriptionPlans,
    staleTime: 1000 * 60 * 5, // Data is considered fresh for 5 minutes
    gcTime: 1000 * 60 * 60, // Data is garbage collected after 1 hour of being unused
    retry: 1, // Retry once on failure
  });
};
